import React, { useEffect, useRef, useState } from 'react';
import { DirectionsRenderer, GoogleMap, Marker, Polyline } from '@react-google-maps/api';
import { useGoogleMaps } from 'packages/novous_web/src/context/GoogleMapContext';
import { IconButton, useMediaQuery, useTheme } from '@mui/material';
import MapViewModal from 'packages/novous_web/src/common/components/MapViewModal';
import mapOption from "packages/novous_web/src/assets/icons/Map Options.svg";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";


const defaultCenter = {
  lat: 28.6139,
  lng: 77.2090,
};

interface TripDetailMapProps {
  routeData: any;
  stopsData: { index: number; lat: number; lng: number }[];
}

const TripDetailMap: React.FC<TripDetailMapProps> = ({ routeData, stopsData }) => {
  const { isLoaded } = useGoogleMaps();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery("(max-width:650px)");
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [mapType, setMapType] = useState<"roadmap" | "satellite" | "terrain" | "hybrid">("roadmap");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', type: 'error' });
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  // Cleanup: Remove previous polyline overlay whenever polylineData changes
  useEffect(() => {
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }
  }, [routeData]);

  useEffect(() => {
    if (routeData) {
      const leg = routeData.directions.routes[0].legs[0];
      setMapCenter(leg.start_location.toJSON());
    } else {
      setMapCenter(defaultCenter);
    }
  }, [routeData]);


  // Determine center: Prefer last stop, then last polyline point, then default
  const lastStop = stopsData.length > 0 ? stopsData[stopsData.length - 1] : null;
  const startPolylinePoint = routeData?.legs ? routeData?.legs[0].start_location : null;
  const lastPolylinePoint = routeData?.legs ? routeData?.legs[routeData?.legs.length-1].end_location : null;

  const center = lastStop || lastPolylinePoint || defaultCenter;

  if (!isLoaded) return <div>Loading Map...</div>;

  const toggleFullscreen = () => {
    console.log("Toggling fullscreen...", containerRef.current);

    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true));
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false));
    }
  };

  // Save polyline instance when loaded
  const handlePolylineLoad = (polyline: google.maps.Polyline) => {
    polylineRef.current = polyline;
  };



  return (
    <>
      <MapViewModal open={open} mapType={mapType} onClose={() => setOpen(false)} onSelect={setMapType} />
      <div ref={containerRef} style={{ position: "relative", width: "100%", height: isSmallScreen ? "400px" : "100%" }}>
        {/* Fullscreen Toggle Button */}
        <div style={{ position: "absolute", top: 10, right: 130, zIndex: 1000 }}>
          <IconButton onClick={toggleFullscreen} sx={{ bgcolor: "white", padding: "8px", boxShadow: 2 }}>
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </div>

        {/* Map Options Button */}
        <div style={{ position: "absolute", top: 10, right: 70, zIndex: 1000 }}>
          <IconButton onClick={() => setOpen(true)} sx={{ bgcolor: "white", padding: "8px", boxShadow: 2 }}>
            <img src={mapOption} width={32} height={32} />
          </IconButton>
        </div>

        <GoogleMap
          mapContainerStyle={{ width: "100%", height: isSmallScreen ? "400px" : "100%" }}
          center={mapCenter}
          zoom={12}
          options={{
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            mapTypeId: mapType,
            keyboardShortcuts: false,
          }}
        >
          {routeData && (
            <DirectionsRenderer
              directions={routeData.directions}
              options={{
                suppressMarkers: true,
                polylineOptions: {
                  strokeColor: theme.palette.primary.main,
                  strokeOpacity: 0.8,
                  strokeWeight: 5,
                  geodesic: true,
                },
              }}
            />
          )
          }

          {/* Start marker */}
          {startPolylinePoint && (
            <Marker
              position={startPolylinePoint}
              label={{ text: 'S', color: 'white' }}

            />
          )}

          {/* Custom Destination Marker */}
          {lastPolylinePoint && (
            <Marker
              position={lastPolylinePoint}
              label={{ text: 'D', color: 'white' }}

            />
          )}

          {/* Plot stops with labels as "Stop (index+1)" */}
          {stopsData.map((stop) => (
            <Marker
              key={stop.index}
              position={{ lat: stop.lat, lng: stop.lng }}
              label={{ text: `S${stop.index + 1}`, color: 'black' }}
            />
          ))}
        </GoogleMap>
      </div>
    </>


  );
};

export default TripDetailMap;

